import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import {
  ENIQ_TIER_PRO,
  ENIQ_TIER_STANDARD,
  getEniqPricePerUser,
} from '../../../../utils/eniqPricing';

/**
 * What separates the two Eniq AI tiers.
 *
 * These describe the add-on itself, so they are the same on every package
 * tier — the package tier only changes the price, which is shown in the header.
 */
export const ENIQ_FEATURES = [
  {
    key: 'models',
    nameKey: 'calculator.eniqCompare.models',
    standard: 'text:calculator.eniqCompare.modelsStandard',
    pro: 'text:calculator.eniqCompare.modelsPro',
  },
  {
    key: 'promptLimit',
    nameKey: 'calculator.eniqCompare.promptLimit',
    standard: 'text:calculator.eniqCompare.promptLimitStandard',
    pro: 'text:calculator.eniqCompare.promptLimitPro',
  },
  {
    key: 'insights',
    nameKey: 'calculator.eniqCompare.insights',
    standard: 'text:calculator.eniqCompare.insightsStandard',
    pro: 'text:calculator.eniqCompare.insightsPro',
  },
  {
    key: 'knowledgeBase',
    nameKey: 'calculator.eniqCompare.knowledgeBase',
    standard: 'text:calculator.eniqCompare.knowledgeBaseStandard',
    pro: 'text:calculator.eniqCompare.knowledgeBasePro',
  },
  {
    key: 'training',
    nameKey: 'calculator.eniqCompare.training',
    standard: 'text:calculator.eniqCompare.trainingStandard',
    pro: 'text:calculator.eniqCompare.trainingPro',
  },
];

const FeatureCell = ({ value, highlight, t }) => {
  if (typeof value === 'string' && value.startsWith('text:')) {
    return (
      <Typography
        sx={{
          fontSize: '0.8rem',
          fontWeight: highlight ? 600 : 500,
          color: highlight ? '#00C853' : '#666',
        }}
      >
        {t(value.replace('text:', ''))}
      </Typography>
    );
  }

  return (
    <Typography sx={{ fontSize: '0.8rem', color: '#999' }}>—</Typography>
  );
};

/**
 * EniqCompareDialog - Standard vs Pro comparison for the Eniq AI add-on.
 *
 * @param {boolean} open
 * @param {function} onClose
 * @param {string} region - Region for pricing (ua, eu, asia, other)
 * @param {string} packageTier - Package tier, decides the price shown per column
 * @param {boolean} [hasPhoneAnchor] - Anchored (21/34) vs standalone (26/39) prices
 * @param {string} selectedTier - Currently selected Eniq tier
 * @param {function} [onTierSelect] - Optional handler; omit for a read-only dialog
 */
const EniqCompareDialog = ({ open, onClose, region, packageTier, hasPhoneAnchor = true, selectedTier, onTierSelect }) => {
  const { t } = useTranslation();
  const symbol = region === 'eu' ? '€' : '$';
  const isReadOnly = !onTierSelect;

  const columns = [
    { key: ENIQ_TIER_STANDARD, name: 'Standard' },
    { key: ENIQ_TIER_PRO, name: 'Pro' },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 1, maxHeight: '85vh' } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('calculator.eniqCompare.title', 'Порівняння тарифів Eniq AI')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(85vh - 100px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    bgcolor: '#fafbfc',
                    width: '40%',
                    borderBottom: '2px solid rgba(0,0,0,0.08)',
                  }}
                >
                  {t('calculator.feature', 'Функція')}
                </TableCell>

                {columns.map((column) => {
                  const isSelected = selectedTier === column.key;
                  const price = getEniqPricePerUser(column.key, packageTier, region, hasPhoneAnchor);

                  return (
                    <TableCell
                      key={column.key}
                      align="center"
                      onClick={isReadOnly ? undefined : () => onTierSelect(column.key)}
                      sx={{
                        fontWeight: 600,
                        bgcolor: isSelected ? '#e8f5e9' : '#fafbfc',
                        borderBottom: isSelected
                          ? '2px solid #00C853'
                          : '2px solid rgba(0,0,0,0.08)',
                        cursor: isReadOnly ? 'default' : 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': isReadOnly ? {} : { bgcolor: '#e0f2e9' },
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, color: isSelected ? '#00C853' : '#222' }}
                      >
                        {column.name}
                      </Typography>

                      {price === 0 ? (
                        <Chip
                          label={t('calculator.included', 'INCLUDED')}
                          size="small"
                          sx={{
                            mt: 0.25,
                            height: 18,
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            bgcolor: 'rgba(0, 200, 83, 0.12)',
                            color: '#00C853',
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: isSelected ? '#00C853' : '#666' }}
                        >
                          {symbol}{price}{t('calculator.perUser', '/user')}
                        </Typography>
                      )}

                      {isSelected && (
                        <Typography
                          variant="caption"
                          sx={{ color: '#00C853', fontWeight: 500, display: 'block' }}
                        >
                          ✓ {t('calculator.selected', 'Вибрано')}
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {ENIQ_FEATURES.map((feature) => (
                <TableRow key={feature.key} hover>
                  <TableCell sx={{ fontSize: '0.8rem', color: '#333' }}>
                    {t(feature.nameKey)}
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell key={column.key} align="center">
                      <FeatureCell
                        value={feature[column.key]}
                        highlight={column.key === ENIQ_TIER_PRO}
                        t={t}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="caption" sx={{ color: '#999' }}>
            {t('calculator.eniqCompare.note', 'Ціна вказана за одного користувача на місяць')}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EniqCompareDialog;
